/**
 * Scenario types and the validator that normalizes them before they are
 * stored on a code component.
 *
 * Multi-state preview scenarios are intended to be stored Webflow-side on
 * the component's `source.meta.states`, produced by the AICC generation
 * flow. They are NOT attached to the author's source and are NOT shipped in
 * the published bundle. The Designer/AI layers that produce and consume
 * them land in later slices of the multi-state visual editing work
 * (#111198); this file only owns the shared type + validator.
 *
 * The shape is intentionally narrow:
 *
 * - `props` is a plain serializable record; deferred state fans out via
 *   the same renderer that already accepts props (no new lifecycle).
 * - We do **not** accept author-supplied render functions. Render
 *   functions can close over variables, capture refs, or run side-effects,
 *   none of which we want to evaluate inside the canvas iframe just to
 *   take a preview snapshot.
 */

/** A single visual configuration of a code component for preview. */
export type ComponentStateScenario = {
  /**
   * Stable, machine-readable id. Used as a wire value across the
   * Designer, the AI Assistant context, and the multi-state DOM.
   *
   * Authors should treat this like a JSX `key` — short, kebab-case, and
   * stable across edits.
   */
  id: string;
  /**
   * Human-readable label shown in the preview header, the navigator
   * tree, and the AI assistant context. Should describe the state from
   * the end-user's perspective ("Step 2: Profile", "Empty cart",
   * "Submitted").
   */
  label: string;
  /**
   * Props passed to the user component for this scenario. Merged on top
   * of any default props the canvas already provides.
   *
   * Must be a plain, JSON-serializable record — it is serialized onto the
   * `<code-island>` host. `extractComponentStateScenarios` clones and
   * freezes it (stripping functions/undefined) before storing.
   *
   * The component author drives state selection by reading these props
   * (e.g. `useState(initialStep ?? 1)`).
   */
  props?: Readonly<Record<string, unknown>>;
};

/** Maximum number of state scenarios honored per component. */
export const MAX_COMPONENT_STATE_SCENARIOS = 12;

export type ExtractComponentStateScenariosOptions = {
  /**
   * Prop names removed from every scenario's `props`. Callers pass the
   * component's Variant prop names: scenario props override the frame's
   * Designer props, so a Variant value here would force every variant
   * column of the grid to render the same variant.
   */
  excludePropKeys?: ReadonlySet<string>;
};

/**
 * Validates and normalizes a list of state scenarios before they are
 * stored on a component.
 *
 * Accepts a bare array of scenarios (the shape the AICC tooling hands the
 * reducer). Returns `[]` when the input is not an array or is empty — the
 * caller treats that as "no multi-state preview available".
 *
 * Trims and drops entries with non-string/blank `id` or `label`, drops any
 * `props` that isn't a plain JSON-serializable object, de-dupes by `id`
 * (first-wins) so a typo in the input can't crash the runtime, and
 * de-dupes by serialized `props` (first-wins) so two scenarios that would
 * produce byte-identical wire props don't render the same cell twice on
 * the canvas. Missing props are treated as an empty prop record for
 * de-duplication (only the first "default state" scenario survives). Caps
 * the list to `MAX_COMPONENT_STATE_SCENARIOS` to avoid pathological input
 * from blowing up the canvas. Keys in `excludePropKeys` are stripped before
 * de-duplication, so states that differed only by an excluded key collapse.
 */
export function extractComponentStateScenarios(
  source: unknown,
  options?: ExtractComponentStateScenariosOptions
): ComponentStateScenario[] {
  if (!Array.isArray(source)) return [];

  const out: ComponentStateScenario[] = [];
  const seenIds = new Set<string>();
  const seenPropKeys = new Set<string>();

  for (const entry of source) {
    if (out.length >= MAX_COMPONENT_STATE_SCENARIOS) break;
    const scenario = normalizeScenario(entry, options?.excludePropKeys);
    if (!scenario) continue;
    if (seenIds.has(scenario.id)) continue;
    // Claim the id before prop de-dupe so a later same-id entry can't rebind
    // it after this one was collapsed for identical props.
    seenIds.add(scenario.id);
    const propKey = canonicalPropKey(scenario.props);
    if (seenPropKeys.has(propKey)) continue;
    seenPropKeys.add(propKey);
    out.push(scenario);
  }

  return out;
}

/**
 * Returns a string key for a scenario's props so two scenarios that would
 * produce byte-identical wire props are treated as equal.
 *
 * Uses the normalized props' actual JSON serialization (preserving key
 * insertion order) rather than sorting keys — components that iterate
 * `Object.keys` / `Object.entries` can render differently when order differs,
 * and the code-island runtime likewise compares by `JSON.stringify` bytes.
 * Missing/undefined props collapse to the same key as an empty record
 * (both render the component with only its default props).
 */
function canonicalPropKey(
  props: Readonly<Record<string, unknown>> | undefined
): string {
  if (!props) return '{}';
  return JSON.stringify(props);
}

/**
 * Returns the keys from `excludePropKeys` that appear in any scenario's
 * `props`, in first-seen order, so callers can report what was stripped.
 */
export function findExcludedScenarioPropKeys(
  source: unknown,
  excludePropKeys: ReadonlySet<string>
): string[] {
  if (!Array.isArray(source) || excludePropKeys.size === 0) return [];
  const found = new Set<string>();
  for (const entry of source) {
    const props = (entry as {props?: unknown} | null)?.props;
    if (!props || typeof props !== 'object' || Array.isArray(props)) continue;
    for (const key of Object.keys(props)) {
      if (excludePropKeys.has(key)) found.add(key);
    }
  }
  return [...found];
}

function normalizeScenario(
  entry: unknown,
  excludePropKeys: ReadonlySet<string> | undefined
): ComponentStateScenario | null {
  if (!entry || typeof entry !== 'object') return null;
  const e = entry as {id?: unknown; label?: unknown; props?: unknown};
  if (typeof e.id !== 'string' || typeof e.label !== 'string') return null;

  const id = e.id.trim();
  const label = e.label.trim();
  if (id.length === 0 || label.length === 0) return null;

  const props = normalizeProps(e.props, excludePropKeys);

  return props === undefined ? {id, label} : {id, label, props};
}

/**
 * Returns a frozen, JSON-serializable clone of `value`, or `undefined` when
 * it isn't a plain object. Cloning (rather than passing the reference
 * through) guarantees the stored props are exactly what will later be
 * JSON-serialized onto the `<code-island>` host, and that the returned
 * scenario doesn't alias — or expose mutations of — the caller's input.
 * Non-serializable input (circular refs) is dropped rather than allowed to
 * throw downstream.
 */
function normalizeProps(
  value: unknown,
  excludePropKeys: ReadonlySet<string> | undefined
): Readonly<Record<string, unknown>> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  try {
    // JSON round-trip strips functions/undefined/symbols and normalizes the
    // record to exactly what ships over the wire.
    const clone: unknown = JSON.parse(JSON.stringify(value));
    // Re-check after serialization: a custom `toJSON` (or a boxed value like
    // a `Date`) can turn a nominal object into a primitive or array, which
    // would violate the record contract downstream.
    if (!clone || typeof clone !== 'object' || Array.isArray(clone)) {
      return undefined;
    }
    const record = clone as Record<string, unknown>;
    if (!excludePropKeys?.size) return Object.freeze(record);
    return Object.freeze(
      Object.fromEntries(
        Object.entries(record).filter(([key]) => !excludePropKeys.has(key))
      )
    );
  } catch {
    return undefined;
  }
}
